import Debug from 'bloko/common/core/Debug';
import TreeHelper from 'bloko/common/tree/treeHelper';
import TreeCollection from 'bloko/common/tree/treeCollection';

/**
 * Возвращает коллекцию, созданную из дерева.
 * @param {Array.<TreeItem>} tree
 * @param {WalkCallback} [callback] Функция, вызываемая на каждом элементе.
 * @returns {TreeCollection}
 */
function fromTree(tree, callback) {
    const collection = new TreeCollection();
    TreeHelper.walk(tree, (item, parents) => {
        if (typeof callback === 'function') {
            callback(item, parents);
        }
        collection.addModel(item, parents.length ? parents[0].id : undefined);
    });
    return collection;
}

/**
 * Возвращает элементы из списка, родители которых отсутствуют в этом же списке.
 * Полезно для определения минимального набора выбранных элементов (если выбран родитель
 * целиком, игнорируем его дочерние элементы).
 * @param {TreeCollection} collection
 * @param {Array.<String>} ids
 * @returns {Array.<String>}
 */
function getIdsWithNoParentsInSameList(collection, ids) {
    const hasChildrenHash = ids.reduce((result, id) => {
        result[id] = collection.getChildren(id).length > 0;
        return result;
    }, {});

    return ids.filter((id) => {
        const parentId = collection.getParentId(id);
        return !parentId || !hasChildrenHash.hasOwnProperty(parentId) || !hasChildrenHash[parentId];
    });
}

/**
 * Возвращает хэш, ключи в котором — ID из списка, имеющие потомков.
 * @param {TreeCollection} collection
 * @param {Array.<String>} ids
 * @returns {Object}
 */
function getParentsHashMap(collection, ids) {
    return ids.reduce((result, id) => {
        collection.getParentIds(id).forEach((parentId) => {
            result[parentId] = true;
        });
        return result;
    }, {});
}

/**
 * Возвращает дерево, состоящее только из элементов с указанными id и всех их предков.
 * @param {TreeCollection} collection
 * @param {Array.<String>} ids
 * @returns {Array.<TreeItem>}
 */
function filterTreeByIdsWithParents(collection, ids) {
    const idsWithParentsHash = getParentsHashMap(collection, ids);
    ids.forEach((id) => {
        idsWithParentsHash[id] = true;
    });
    return collection.toTree((model) => {
        return idsWithParentsHash.hasOwnProperty(model.id);
    });
}

/**
 * Возвращает только те ID, которые присутствуют в коллекции, и логирует ошибки.
 * @param {TreeCollection} collection
 * @param {Array.<String>} ids
 * @returns {Array}
 */
function filterMissingIds(collection, ids) {
    const filteredIds = [];
    ids.forEach((id) => {
        if (collection.getModel(id)) {
            filteredIds.push(id);
        } else {
            Debug.log('out error', new Error(`ID "${id}" is missing in the collection`));
        }
    });
    return filteredIds;
}

/**
 * Возвращает ID только тех элементов, у которых нет потомков.
 * @param {TreeCollection} collection
 * @param {Array.<String>} ids
 * @returns {Array.<String>}
 */
function filterParents(collection, ids) {
    return ids.filter((id) => {
        return collection.getChildrenIds(id).length === 0;
    });
}

export {
    fromTree,
    getParentsHashMap,
    getIdsWithNoParentsInSameList,
    filterTreeByIdsWithParents,
    filterMissingIds,
    filterParents,
};
